<template>
  <div class="policy-layout">
    <AsyncIpBlocked v-if="ipBlock" />
    <template v-else>
      <BasicHeader />
      <div class="wrapper">
        <main :style="mainStyle">
          <!-- 这里加 key 的原因是，在移动端，从 /browser-extension 使用中间件跳转到首页时 slot 中的 DOM 结构没有完全改变 -->
          <!-- 仍然保存了部分 /browser-extension 的结构，所以使用 pathNoLocale 作为 key 强制 URL 改变时渲染-->
          <PolicyNav />

          <div class="content">
            <!-- 如果是"更新提示"页面 -->
            <h1
              v-if="policyDate && isUpdatePage"
              class="title"
            >
              {{ `Updates Effective on ${policyDate}: ${$t(policyName)}` }}
            </h1>

            <!-- 如果是更新提示页面, url query 带了 ?statement=1 -->
            <p
              v-if="policyDate && statementVisible && isUpdatePage"
              class="statement"
              v-html="$t('8AaXinCTedLgVqEpyd', [$t(policyName)])"
            />

            <!-- 如果不是更新提示页面, 且不是最新生效版本 -->
            <p v-if="isNotLatestEffectiveVersion && !isUpdatePage">
              This may be an archived version. View the
              <NuxtLinkLocale :to="`/policy${policyMap[policyUpdate.name].path}`"> current version </NuxtLinkLocale>
              .
            </p>

            <!-- 如果不是更新提示页面, 且不是最新生效版本, 且不是最新版本 -->
            <p v-if="!isNotLatestEffectiveVersion && isNotLatestVersion && !isUpdatePage">
              There is a
              <NuxtLinkLocale :to="prevRoute"> updated version </NuxtLinkLocale>
              that will take effect later.
            </p>
            <Empty />

            <slot :key="pathNoLocale" />

            <!-- 如果是更新提示页面 -->
            <ArchivedVersion
              v-if="policyDate && isUpdatePage"
              :date="policyDate"
              :next-route="nextRoute"
              :prev-route="updatedRoute"
            />
          </div>
        </main>
      </div>
    </template>
    <LoginDialog ref="loginDialog" />
  </div>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, provide, ref, toTimestamp, useI18n, useSeoMeta, watch } from '#imports'
import Empty from '@/components/policy/Empty.vue'
import PolicyNav from '@/components/PolicyNav.vue'
import LoginDialog from '@/layouts/common/LoginDialog.vue'
import { useLayout } from '@/store/layout'
import { useUserInfo } from '@/store/userInfo'
import $track from '@/track/webOfficialWebsite'
import { useRoute } from 'vue-router'
import ArchivedVersion from '~/components/policy/ArchivedVersion.vue'
import usePathNoLocale from '~/composables/usePathNoLocale'
import { policyMap, type PolicyUpdate, versionList } from '~/config/policy'
import BasicHeader from '~/layouts/basic/BasicHeader.vue'
import { OG_SITE_NAME } from '~/types/constants'
import { InjectionKey, PolicyName, RoutePath } from '~/types/enum'

const { t } = useI18n()

useSeoMeta({
  ogSiteName: OG_SITE_NAME,
  description: () => t('page-description'),
  ogType: 'website',
  ogDescription: () => t('page-description'),
})
const pathNoLocale = usePathNoLocale()
const route = useRoute()
const layout = useLayout()

const mainStyle = computed(() => (pathNoLocale.value === RoutePath.HOME ? undefined : { marginTop: '60px' }))
const AsyncIpBlocked = defineAsyncComponent(() => import('@/components/IpBlocked.vue'))

const loginDialog = ref<InstanceType<typeof LoginDialog>>()
const showLoginDialog: ShowLoginDialog = callback => loginDialog.value?.show(callback)

provide<ShowLoginDialog>(InjectionKey.SHOW_LOGIN_DIALOG, showLoginDialog)
const ipBlock = computed(() => !!useUserInfo().ipBlock)

// 上报页面展示
const trackOfficialPageShow = (_path: RoutePath) => {
  switch (_path) {
    case RoutePath.HOME:
      return
    case RoutePath.MOBILE_APP:
      return $track.web_official_mobile_page_show()
    case RoutePath.DESKTOP_APP:
      return $track.web_official_desktop_page_show()
    case RoutePath.BROWSER_EXTENSION:
      return $track.web_official_extension_page_show()
    case RoutePath.HELP:
      return $track.web_official_help_page_show()
    case RoutePath.FAQ:
      return $track.web_official_faq_page_show()
    default:
      break
  }
}

// 当前协议的更新list
const policyUpdate = ref<PolicyUpdate>({ date: '', name: PolicyName.POLICY_UPDATES })

// 是否显示更新提示 query.statement=1
const statementVisible = ref(false)

// 判断当前是否是最新有效版本，及前后版本

// 当前协议的更新list
const currentPolicyUpdates = computed(() => versionList.filter(({ name }) => name === policyUpdate.value.name))

// 当前展示协议index,不一定是最新的，主要用来定位前后版本
const currentIndex = computed(() => {
  // 在协议的更新list中找到当前版本序号
  const index = currentPolicyUpdates.value.findIndex(
    ({ date, name }) => date === policyUpdate.value.date && policyUpdate.value.name === name,
  )
  // 如果找不到当前版本序号
  if (index === -1) {
    // 找到最新版本序号， 没有版本就是 -1
    return currentPolicyUpdates.value.findIndex(
      ({ name, date }) => name === policyUpdate.value.name && toTimestamp(date) < layout.serverTime,
    )
  }
  return index
})
// 获取政策名称
const policyName = computed(() => policyMap[policyUpdate.value.name]?.title)
// 获取政策日期
const policyDate = computed(() => policyUpdate.value.date || currentPolicyUpdates.value[currentIndex.value!]?.date)

// 是否不是最新版本 (当前日期版本和最新日期版本不匹配)
const isNotLatestVersion = computed(
  () =>
    (policyUpdate.value?.name !== currentPolicyUpdates.value[0]?.name ||
      policyDate.value !== currentPolicyUpdates.value[0]?.date) &&
    !!policyDate.value,
)

// 是否非最新的生效中版本
const isNotLatestEffectiveVersion = computed(() => {
  // 从更新list中找到前一个版本
  const prevVersion = currentPolicyUpdates.value[currentIndex.value - 1]
  // 如果前一个版本存在， 且前一个版本日期小于当前时间， 说明不是最新生效版本
  return prevVersion ? toTimestamp(prevVersion.date) < layout.serverTime : false
})
// 获取最新版本路由， 没有版本路径会是 /undefined
const updatedRoute = computed(
  () => `/policy${policyMap[policyUpdate.value.name].path}/${currentPolicyUpdates.value[currentIndex.value]?.date}`,
)
// 获取前一个版本路由 （针对翻页，前一个版本日期更大）
const prevRoute = computed(
  () =>
    `/policy${policyMap[policyUpdate.value.name].path}/${
      currentPolicyUpdates.value[currentIndex.value === 0 ? currentIndex.value : currentIndex.value - 1]?.date
    }`,
)

// 获取下一个版本路由
const nextRoute = computed(
  () =>
    `/policy${policyMap[policyUpdate.value.name].path}/${
      currentPolicyUpdates.value[
        currentIndex.value === currentPolicyUpdates.value.length - 1 ? currentIndex.value : currentIndex.value + 1
      ]?.date
    }`,
)
const isUpdatePage = ref(false)

watch(
  route,
  val => {
    const { statement } = val.query
    statementVisible.value = statement === '1'
  },
  { immediate: true },
)

// 监听路由变化 - 设置当前页面
watch(
  pathNoLocale,
  async val => {
    // 上报页面展示
    import.meta.client && trackOfficialPageShow(val)
    // 是否是更新页面
    isUpdatePage.value = false
    // 获取路由路径
    const routePath = val.split('/')
    // 是否是更新模块页面
    if (routePath.includes('update')) isUpdatePage.value = true
    // 获取政策名称和日期
    const name = routePath[isUpdatePage.value ? 3 : 2] as PolicyName
    const date = routePath[isUpdatePage.value ? 4 : 3]
    // 设置政策名称和日期
    policyUpdate.value = {
      name,
      date,
    }
  },
  { immediate: true },
)
</script>

<style lang="less" scoped>
@import '@/assets/styles/util.less';

.policy-layout {
  display: flex;
  flex-direction: column;
  min-height: var(--vh, 100vh);
  background-color: rgb(245, 246, 250);
}

main {
  max-width: 1400px;
  display: flex;
  margin: 0 auto;
}

.content {
  background-color: #fff;
  margin: 12px 0 12px 18px;
  /* transform: translateX(-100px); */
  border-radius: 4px;
  padding: 32px 42px;
  min-height: calc(100vh - 130px);
}

p {
  font-size: 14px;
  line-height: 21px;
}

h1 {
  margin: 10px 0;
  font-size: 24px;
  line-height: 1.4em;
  font-weight: bold;
}

@media screen and (max-width: @mobile-break-width) {
  .policy-layout {
    background: #fff;
  }

  main {
    flex-direction: column;
    padding: 24px 16px;
    margin-top: 60px !important;
  }

  .content {
    padding: 0;
    margin: 0;
  }
}
</style>
<style lang="less">
.policy {
  font-size: 14px;
  line-height: 21px;

  ul,
  ol,
  li {
    list-style: revert;
    margin-left: 1em;
  }

  li {
    line-height: 1.5em;
  }

  h1 {
    margin: 10px 0;
    font-size: 24px;
    line-height: 1.4em;
  }

  h2 {
    font-size: 14px;
    font-weight: bold;
    margin: 16px 0;
  }

  p {
    line-height: 1.4em;
    margin: 16px 0;
  }

  strong {
    font-weight: bold;
  }

  .date {
    font-size: 12px;
    color: #666;
    margin: 12px 0 0;
  }
}
</style>
